import { useState, useEffect, useMemo, useCallback } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Loader2, RotateCcw, Save, ShieldCheck, ShieldAlert } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Canonical list of resources and the actions we surface per resource.
const RESOURCES = [
  { key: "dashboard", label: "Dashboard", actions: ["read"] },
  { key: "clients", label: "Clients", actions: ["create", "read", "update", "delete"] },
  { key: "employees", label: "Employees", actions: ["create", "read", "update", "delete"] },
  { key: "physicians", label: "Physicians", actions: ["create", "read", "update", "delete"] },
  { key: "schedule", label: "Schedule", actions: ["create", "read", "update", "delete"] },
  { key: "evv", label: "EVV", actions: ["read", "verify", "edit", "export", "clock_in", "clock_out"] },
  { key: "referrals", label: "Referrals", actions: ["create", "read", "update", "delete"] },
  { key: "order_tracking", label: "Order Tracking", actions: ["read", "update"] },
  { key: "communications", label: "Communications", actions: ["create", "read", "send"] },
  { key: "hr_payroll", label: "HR & Payroll", actions: ["create", "read", "update"] },
  { key: "evaluations", label: "Evaluations", actions: ["create", "read", "update", "delete"] },
  { key: "settings", label: "Settings", actions: ["read", "update"] },
  { key: "billing", label: "Billing", actions: ["read", "update"] },
  { key: "users", label: "Users", actions: ["create", "read", "update", "delete"] },
  { key: "reports", label: "Reports", actions: ["read", "export"] },
  { key: "documents", label: "Documents / Files", actions: ["read", "download", "upload", "delete"] },
];

const ACTION_LABELS = {
  read: "View",
  create: "Create",
  update: "Edit",
  delete: "Delete",
  verify: "Verify",
  edit: "Edit",
  export: "Export",
  send: "Send",
  clock_in: "Clock In",
  clock_out: "Clock Out",
  download: "Download / Print",
  upload: "Upload",
};

/**
 * Reusable permissions editor for a single user. Shows role baseline and lets
 * an admin toggle grants/revocations per resource.action. Diffs against the
 * baseline to compute the `overrides` payload sent to the API.
 *
 * Props:
 *   userId          — the target user's UUID (required)
 *   canEdit         — bool; when false, renders in read-only mode
 *   compact         — bool; smaller styling for embedding
 */
export default function EmployeePermissionsPanel({ userId, canEdit = true, compact = false }) {
  const [data, setData] = useState(null);          // { role, role_permissions, overrides, effective, ... }
  const [checked, setChecked] = useState({});      // {resource: {action: bool}} — CURRENT desired state
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const fetchPermissions = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${API}/users/${userId}/permissions`);
      setData(res.data);
      // Seed the check-state from effective permissions
      const seed = {};
      Object.entries(res.data.effective || {}).forEach(([res_, acts]) => {
        seed[res_] = {};
        (acts || []).forEach((a) => { seed[res_][a] = true; });
      });
      setChecked(seed);
    } catch (err) {
      console.error("Failed to load permissions", err);
      setError(err.response?.data?.detail || "Failed to load permissions");
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => { fetchPermissions(); }, [fetchPermissions]);

  // Compute overrides = diff between current checked-state and role baseline
  const overrides = useMemo(() => {
    if (!data) return {};
    const base = {};
    Object.entries(data.role_permissions || {}).forEach(([res, acts]) => {
      base[res] = new Set(acts || []);
    });
    const diff = {};
    RESOURCES.forEach(({ key: res, actions }) => {
      actions.forEach((act) => {
        const baseHas = base[res]?.has(act) || false;
        const nowHas = !!checked[res]?.[act];
        if (baseHas !== nowHas) {
          diff[res] = diff[res] || {};
          diff[res][act] = nowHas;
        }
      });
    });
    return diff;
  }, [data, checked]);

  const overrideCount = useMemo(
    () => Object.values(overrides).reduce((n, actions) => n + Object.keys(actions).length, 0),
    [overrides]
  );

  const toggle = (res, act) => {
    setChecked((prev) => ({
      ...prev,
      [res]: { ...(prev[res] || {}), [act]: !prev[res]?.[act] },
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/users/${userId}/permissions`, { overrides });
      toast.success(`Permissions saved (${overrideCount} override${overrideCount === 1 ? "" : "s"})`);
      await fetchPermissions();
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.detail || "Failed to save permissions");
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    setSaving(true);
    try {
      await axios.delete(`${API}/users/${userId}/permissions`);
      toast.success("Reverted to role defaults");
      await fetchPermissions();
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.detail || "Failed to reset");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8 text-slate-500">
        <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading permissions…
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-md border border-red-200 bg-red-50 p-4 text-sm text-red-700">
        {error}
      </div>
    );
  }

  if (!data) return null;

  if (data.role === "admin") {
    return (
      <div className="rounded-md border border-teal-200 bg-teal-50 p-4 text-sm text-teal-800 flex items-start gap-3">
        <ShieldCheck className="w-5 h-5 mt-0.5 shrink-0" />
        <div>
          <div className="font-medium">Administrator</div>
          <div className="text-teal-700">
            Administrators have full access to all features and cannot be restricted.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={compact ? "space-y-3" : "space-y-4"} data-testid="employee-permissions-panel">
      <div className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm">
        <div className="flex items-center gap-2">
          <ShieldAlert className="w-4 h-4 text-slate-500" />
          <span className="text-slate-700">
            Role baseline: <span className="font-medium capitalize">{(data.role || "").replace(/_/g, " ")}</span>
          </span>
          {overrideCount > 0 && (
            <Badge className="bg-amber-100 text-amber-800">
              {overrideCount} unsaved override{overrideCount === 1 ? "" : "s"}
            </Badge>
          )}
        </div>
        {canEdit && (
          <div className="flex items-center gap-2">
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  className="gap-1.5"
                  disabled={saving || (Object.keys(data.overrides || {}).length === 0)}
                  data-testid="reset-permissions-btn"
                >
                  <RotateCcw className="w-3.5 h-3.5" />
                  Reset to role
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Clear all custom permissions?</AlertDialogTitle>
                  <AlertDialogDescription>
                    This employee will go back to their role&apos;s default permissions. Any per-user grants or revocations you&apos;ve saved will be removed.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={handleReset} className="bg-red-600 hover:bg-red-700">
                    Reset
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
            <Button
              size="sm"
              className="gap-1.5 bg-teal-600 hover:bg-teal-700"
              onClick={handleSave}
              disabled={saving || overrideCount === 0}
              data-testid="save-permissions-btn"
            >
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
              Save changes
            </Button>
          </div>
        )}
      </div>

      <div className="overflow-hidden rounded-md border border-slate-200">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-600">
            <tr>
              <th className="text-left px-3 py-2 font-medium w-1/3">Resource</th>
              <th className="text-left px-3 py-2 font-medium">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {RESOURCES.map(({ key: res, label, actions }) => {
              const baseActs = new Set(data.role_permissions?.[res] || []);
              return (
                <tr key={res}>
                  <td className="px-3 py-3 align-top">
                    <div className="font-medium text-slate-800">{label}</div>
                    {baseActs.size === 0 && (
                      <div className="text-xs text-slate-400 mt-0.5">Not in role by default</div>
                    )}
                  </td>
                  <td className="px-3 py-3">
                    <div className="flex flex-wrap gap-x-4 gap-y-2">
                      {actions.map((act) => {
                        const baseHas = baseActs.has(act);
                        const nowHas = !!checked[res]?.[act];
                        const isOverride = baseHas !== nowHas;
                        return (
                          <label
                            key={act}
                            className={`flex items-center gap-2 rounded px-2 py-1 ${isOverride ? "bg-amber-50 ring-1 ring-amber-200" : ""}`}
                          >
                            <Switch
                              checked={nowHas}
                              onCheckedChange={() => canEdit && toggle(res, act)}
                              disabled={!canEdit}
                              data-testid={`perm-toggle-${res}-${act}`}
                            />
                            <span className="text-slate-700">
                              {ACTION_LABELS[act] || act}
                            </span>
                            {isOverride && (
                              <span className="text-[10px] uppercase font-semibold text-amber-700">
                                custom
                              </span>
                            )}
                          </label>
                        );
                      })}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <p className="text-xs text-slate-500">
        Toggles show the effective permission for this employee. Anything different from the role baseline is highlighted as{" "}
        <span className="text-amber-700 font-semibold uppercase">custom</span>. Save to apply overrides.
      </p>
    </div>
  );
}
